import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import '../styles/ProductModal.css';

const ProductModal = ({ product, onClose }) => {
  const { addToCart } = useCart();

  const types = product?.types || [];
  const initialType = types.length > 0 ? types[0].name : '';
  const firstTypeObj = types.find((t) => t.name === initialType);
  const initialImg =
    firstTypeObj && firstTypeObj.image && firstTypeObj.image !== 'N/A'
      ? firstTypeObj.image
      : (product?.gallery && product.gallery.length > 0 ? product.gallery[0] : product?.image);

  const [selectedType, setSelectedType] = useState(initialType);
  const [selectedImage, setSelectedImage] = useState(initialImg || product?.image);
  const [customNote, setCustomNote] = useState('');

  if (!product) return null;

  const handleTypeSelect = (typeName) => {
    setSelectedType(typeName);
    const matchedType = types.find((t) => t.name === typeName);
    if (matchedType && matchedType.image && matchedType.image !== 'N/A') {
      setSelectedImage(matchedType.image);
    } else {
      setSelectedImage(product.image);
    }
  };

  const handleThumbnailClick = (img) => {
    setSelectedImage(img);
    if (product.types) {
      const match = product.types.find((t) => t.image === img);
      if (match) {
        setSelectedType(match.name);
      }
    }
  };

  const getImageLabel = (imgUrl) => {
    if (product.types) {
      const match = product.types.find((t) => t.image === imgUrl);
      if (match) return match.name;
    }
    if (product.galleryDetails) {
      const match = product.galleryDetails.find((item) => item.url === imgUrl);
      if (match) return match.label;
    }
    return product.name;
  };

  const handleAddToCart = () => {
    if (!product.inStock) return;

    const selectedOptions = {};
    if (selectedType) {
      selectedOptions.type = selectedType;
    }
    if (customNote.trim()) {
      selectedOptions.customNote = customNote.trim();
    }

    const matchedType = (product.types || []).find((t) => t.name === selectedType);
    const chosenVariationId = matchedType?.squareVariationId || product.squareVariationId || null;

    addToCart({ ...product, squareVariationId: chosenVariationId }, selectedOptions);
    onClose();
  };

  const rawGallery = [
    product.image,
    ...(product.gallery || []),
    ...((product.types || []).map((t) => t.image).filter(Boolean)),
  ].filter((img) => img && img !== 'N/A');
  const galleryImages = Array.from(new Set(rawGallery));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close modal">&times;</button>
        
        <div className="modal-grid">
          {/* Left Column: Product Media */}
          <div className="modal-media">
            <div className="modal-main-image" title={getImageLabel(selectedImage)}>
              <img src={selectedImage} alt={getImageLabel(selectedImage)} />
              {galleryImages.length > 1 && (
                <div className="modal-hover-label">{getImageLabel(selectedImage)}</div>
              )}
            </div>
            
            {galleryImages.length > 1 && (
              <div className="modal-gallery-thumbs">
                {galleryImages.map((img, idx) => {
                  const label = getImageLabel(img);
                  return (
                    <img
                      key={idx}
                      src={img}
                      alt={label}
                      title={label}
                      className={`thumb ${selectedImage === img ? 'active' : ''}`}
                      onClick={() => handleThumbnailClick(img)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Column: Details & Configuration */}
          <div className="modal-details">
            <div className="product-badges-row">
              {product.category && (
                <span className="badge category-badge">{product.category}</span>
              )}
              <span className={`badge stock-badge ${product.inStock ? 'in-stock' : 'sold-out'}`}>
                {product.inStock ? 'In Stock' : 'Sold Out'}
              </span>
            </div>

            <h2>{product.name}</h2>
            <div className="modal-price">{product.price}</div>
            {product.description && (
              <p className="modal-description">{product.description}</p>
            )}

            {/* Dynamic Type / Variation Selection */}
            {product.types && product.types.length > 0 && (
              <div className="option-group">
                <label className="option-label">
                  Style / Variation:
                </label>
                <select
                  className="modal-select"
                  value={selectedType}
                  onChange={(e) => handleTypeSelect(e.target.value)}
                >
                  {product.types.map((typeObj, i) => (
                    <option key={i} value={typeObj.name} disabled={typeObj.inStock === false}>
                      {typeObj.name} {typeObj.inStock === false ? '(Sold Out)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Customization Options */}
            {product.isCustomizable && (
              <div className="option-group">
                <label className="option-label" htmlFor="modalCustomNote">
                  Special Instructions / Bias Notes:
                </label>
                <input
                  id="modalCustomNote"
                  type="text"
                  className="modal-input"
                  placeholder="Enter member, group, colors, or special request..."
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="modal-actions">
              <button 
                type="button"
                className="button modal-add-btn" 
                onClick={handleAddToCart}
                disabled={!product.inStock}
              >
                {!product.inStock ? 'Sold Out' : 'Add to Cart'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
